import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, BadgeCheck, BedDouble, Check, ChevronDown, FileText,
  HandPlatter, Headphones, HeartHandshake, KeyRound, LayoutGrid, Loader2, Megaphone,
  Sparkles, TrendingUp, Wrench,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage, scrollToSection } from "../i18n";
import { useSEO } from "../seo";
import { IMAGES, LINKS, trackEvent } from "../data";
import { SERVICES } from "./services/content";
import Quiz from "./services/Quiz";
import BeforeAfter from "./services/BeforeAfter";
import Calculator from "./services/Calculator";
import ReviewsSection from "./services/ReviewsSection";
import { ServicesCatalog } from "./services/Catalog";
import { ContactSection } from "./services/ContactSection";
import { CONTACT, PATHS } from "./services/catalog";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm font-semibold text-ink placeholder-mute/70 transition-[border-color,box-shadow] duration-300 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/25";

const HOTEL_ICONS = [BedDouble, Sparkles, HandPlatter, LayoutGrid, Wrench, HeartHandshake];
const DEPT_ICONS = [TrendingUp, Headphones, Sparkles, Wrench, Megaphone, FileText];
const TYPE_MAP = [2, 1, 0, 3, 4, 6];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ---------------- counter animato (con fallback: mostra sempre il valore finale) ---------------- */
function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const fallback = setTimeout(() => setVal(to), duration + 600);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fallback);
    };
  }, [inView, to]);
  return (
    <span ref={ref} className="tabular-nums">
      {val.toLocaleString("it-IT")}
      {suffix}
    </span>
  );
}

function SectionHead({ eyebrow, title, titleItalic, sub, dark = false, center = false, testid }) {
  return (
    <Reveal>
      <div className={center ? "text-center" : ""} data-testid={testid}>
        {eyebrow && (
          <p className={`kicker ${dark ? "text-sky" : "text-navy"} ${center ? "justify-center" : ""}`}>
            <span className="h-px w-10 bg-sky" />
            {eyebrow}
          </p>
        )}
        <h2 className={`mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl ${dark ? "text-white" : "text-navy"}`}>
          {title}
          {titleItalic && <span className="block italic text-med">{titleItalic}</span>}
        </h2>
        {sub && <p className={`mt-4 max-w-xl text-base leading-relaxed ${dark ? "text-white/65" : "text-mute"} ${center ? "mx-auto" : ""}`}>{sub}</p>}
      </div>
    </Reveal>
  );
}

/* ---------------- FORM DETTAGLIATO (multistep) — secondo passaggio opzionale ---------------- */
const EMPTY_FORM = {
  name: "", surname: "", phone: "", email: "", contact_pref: "",
  location: "", property_type: "", bedrooms: "", beds: "", online: "", airbnb_link: "", message: "", website: "",
};

const FV_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function FinalForm({ f, lang, quizAnswers }) {
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const set = (k) => (e) => {
    const v = e.target?.value ?? e;
    setForm((s) => ({ ...s, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  useEffect(() => {
    if (!quizAnswers) return;
    const q = SERVICES[lang].quiz.questions;
    const typeIdx = q[1].options.indexOf(quizAnswers[1]);
    const onlineIdx = q[2].options.indexOf(quizAnswers[2]);
    setForm((s) => ({
      ...s,
      location: quizAnswers[0] === q[0].options[4] ? s.location : quizAnswers[0],
      property_type: typeIdx >= 0 ? f.types[TYPE_MAP[typeIdx]] : s.property_type,
      online: onlineIdx >= 0 ? f.onlineOpts[onlineIdx] : s.online,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizAnswers]);

  const validateStep = (s) => {
    const er = {};
    if (s === 0 && form.location.trim().length < 2) er.location = f.required;
    if (s === 1 && !form.property_type) er.property_type = f.required;
    if (s === 4) {
      if (form.name.trim().length < 2) er.name = f.required;
      if (form.surname.trim().length < 2) er.surname = f.required;
      if (form.phone.trim().length < 5) er.phone = f.required;
      if (!FV_EMAIL_RE.test(form.email.trim())) er.email = f.emailError;
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const next = () => {
    if (validateStep(step)) setStep((v) => Math.min(4, v + 1));
  };
  const back = () => setStep((v) => Math.max(0, v - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep(4)) return;
    setSending(true);
    try {
      await axios.post(`${API}/valuations`, {
        name: `${form.name} ${form.surname}`.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        location: form.location.trim(),
        property_type: form.property_type,
        contact_pref: form.contact_pref || null,
        bedrooms: form.bedrooms || null,
        beds: form.beds || null,
        status: form.online || null,
        airbnb_link: form.airbnb_link || null,
        message: form.message || null,
        website: form.website || null,
        locale: lang,
      });
      trackEvent("owner_valuation_request", { source: "servizi_dettagliato" });
      setSent(true);
      toast.success(f.success);
    } catch {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  const resetAll = () => {
    setSent(false);
    setStep(0);
    setErrors({});
    setForm({ ...EMPTY_FORM });
  };

  const selectCls = `${inputCls} appearance-none`;
  const pillCls = (active) =>
    `rounded-full px-5 py-2.5 text-xs font-bold transition-colors duration-300 ${
      active ? "bg-navy text-white" : "bg-white text-mute ring-1 ring-navy/15 hover:text-navy"
    }`;
  const err = (k) => errors[k] && (
    <p data-testid={`fv-error-${k}`} role="alert" className="text-xs font-bold text-red-600">{errors[k]}</p>
  );
  const labelCls = "text-xs font-extrabold uppercase tracking-[0.15em] text-navy";
  const progress = Math.round(((step + 1) / 5) * 100);

  if (sent) {
    return (
      <div data-testid="fv-success-panel" className="rounded-[2rem] bg-warm p-9 text-center shadow-2xl sm:p-12">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sky/15 text-navy">
          <BadgeCheck className="h-8 w-8" />
        </span>
        <h3 className="mt-6 font-display text-2xl font-semibold text-navy">{f.successTitle}</h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-mute">{f.successBody}</p>
        <button data-testid="fv-success-again" onClick={resetAll} className="btn-secondary mt-8 !px-6 !py-3 text-[13px]">
          {f.successAgain}
        </button>
      </div>
    );
  }

  return (
    <form data-testid="final-valuation-form" onSubmit={submit} noValidate className="rounded-[2rem] bg-warm p-7 text-navy shadow-2xl sm:p-9">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h3 className="font-display text-xl font-semibold text-navy">{f.title}</h3>
        <span data-testid="fv-step-indicator" className="text-[10px] font-extrabold uppercase tracking-wider text-mute">
          {f.step} {step + 1} {f.of} 5
        </span>
      </div>
      <div className="mb-7 h-1.5 overflow-hidden rounded-full bg-navy/10">
        <div data-testid="form-progress-bar" className="h-full rounded-full bg-sky transition-[width] duration-500" style={{ width: `${progress}%` }} />
      </div>
      {quizAnswers && (
        <p data-testid="form-prefilled-note" className="mb-5 inline-flex items-center gap-2 rounded-full bg-sky/15 px-4 py-1.5 text-xs font-bold text-navy">
          <BadgeCheck className="h-3.5 w-3.5" />
          {f.prefilled}
        </p>
      )}

      <p className="mb-5 font-display text-lg font-semibold text-navy" data-testid={`fv-step-title-${step}`}>{f.steps[step]}</p>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.3, ease: EASE }}
        >
          {step === 0 && (
            <div className="grid gap-2" data-testid="fv-step-0">
              <label className="grid gap-2">
                <span className={labelCls}>{f.location} *</span>
                <input data-testid="fv-location-input" name="location" autoComplete="address-level2" value={form.location} onChange={set("location")} placeholder={f.locationPh} className={inputCls} />
              </label>
              {err("location")}
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-2" data-testid="fv-step-1">
              <span className={labelCls}>{f.type} *</span>
              <div className="flex flex-wrap gap-2">
                {f.types.map((tp, i) => (
                  <button key={tp} type="button" data-testid={`fv-type-option-${i}`} onClick={() => set("property_type")(tp)} className={pillCls(form.property_type === tp)}>
                    {tp}
                  </button>
                ))}
              </div>
              {err("property_type")}
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-5 sm:grid-cols-2" data-testid="fv-step-2">
              <label className="grid gap-2">
                <span className={labelCls}>{f.bedrooms}</span>
                <select data-testid="fv-bedrooms-select" name="bedrooms" value={form.bedrooms} onChange={set("bedrooms")} className={selectCls}>
                  <option value="">—</option>
                  {f.bedroomsOpts.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <label className="grid gap-2">
                <span className={labelCls}>{f.beds}</span>
                <select data-testid="fv-beds-select" name="beds" value={form.beds} onChange={set("beds")} className={selectCls}>
                  <option value="">—</option>
                  {f.bedsOpts.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-5" data-testid="fv-step-3">
              <div className="grid gap-2">
                <span className={labelCls}>{f.online}</span>
                <div className="flex flex-wrap gap-2">
                  {f.onlineOpts.map((opt, i) => (
                    <button key={opt} type="button" data-testid={`fv-online-option-${i}`} onClick={() => set("online")(opt)} className={pillCls(form.online === opt)}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <label className="grid gap-2">
                <span className={labelCls}>{f.link}</span>
                <input data-testid="fv-link-input" name="airbnb_link" type="url" inputMode="url" value={form.airbnb_link} onChange={set("airbnb_link")} placeholder={f.linkPh} className={inputCls} />
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="grid gap-5 sm:grid-cols-2" data-testid="fv-step-4">
              <div className="grid gap-2">
                <label className="grid gap-2">
                  <span className={labelCls}>{f.name} *</span>
                  <input data-testid="fv-name-input" name="given-name" autoComplete="given-name" value={form.name} onChange={set("name")} placeholder={f.namePh} className={inputCls} />
                </label>
                {err("name")}
              </div>
              <div className="grid gap-2">
                <label className="grid gap-2">
                  <span className={labelCls}>{f.surname} *</span>
                  <input data-testid="fv-surname-input" name="family-name" autoComplete="family-name" value={form.surname} onChange={set("surname")} placeholder={f.surnamePh} className={inputCls} />
                </label>
                {err("surname")}
              </div>
              <div className="grid gap-2">
                <label className="grid gap-2">
                  <span className={labelCls}>{f.phone} *</span>
                  <input data-testid="fv-phone-input" name="tel" autoComplete="tel" type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder={f.phonePh} className={inputCls} />
                </label>
                {err("phone")}
              </div>
              <div className="grid gap-2">
                <label className="grid gap-2">
                  <span className={labelCls}>{f.email} *</span>
                  <input data-testid="fv-email-input" name="email" autoComplete="email" type="email" inputMode="email" value={form.email} onChange={set("email")} placeholder={f.emailPh} className={inputCls} />
                </label>
                {err("email")}
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <span className={labelCls}>{f.contactPref}</span>
                <div className="flex flex-wrap gap-2">
                  {f.contactOpts.map((opt) => (
                    <button key={opt} type="button" data-testid={`fv-contact-${opt.toLowerCase()}`} onClick={() => set("contact_pref")(opt)} className={pillCls(form.contact_pref === opt)}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <label className="grid gap-2 sm:col-span-2">
                <span className={labelCls}>{f.message}</span>
                <textarea data-testid="fv-message-input" name="message" rows={3} value={form.message} onChange={set("message")} placeholder={f.messagePh} className={`${inputCls} resize-none`} />
              </label>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* honeypot anti-spam */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
      />

      <div className="mt-8 flex items-center gap-3">
        {step > 0 && (
          <button type="button" data-testid="fv-back-button" onClick={back} className="btn-secondary !px-6 !py-3.5 text-[13px]">
            <ArrowLeft className="h-4 w-4" />
            {f.back}
          </button>
        )}
        {step < 4 ? (
          <button type="button" data-testid="fv-next-button" onClick={next} className="btn-primary flex-1 !py-3.5 text-sm">
            {f.next}
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button data-testid="fv-submit-button" type="submit" disabled={sending} className="btn-primary flex-1 !py-3.5 text-sm disabled:opacity-60">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            {sending ? f.sending : f.submit}
          </button>
        )}
      </div>
    </form>
  );
}

/* ---------------- PAGINA ---------------- */
export default function ServicesPage() {
  const { lang } = useLanguage();
  const s = SERVICES[lang];
  const f = s.form;
  const contact = CONTACT[lang];
  const [quizAnswers, setQuizAnswers] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [openService, setOpenService] = useState(null);
  const [tierOpen, setTierOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const formVisible = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => scrollToSection(location.hash), 500);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  useSEO(s.seo.title, s.seo.description);

  useEffect(() => {
    const target = document.getElementById("valutazione");
    if (!target) return;
    const obs = new IntersectionObserver(([e]) => {
      formVisible.current = e.isIntersecting;
      setShowSticky(!e.isIntersecting && window.scrollY > window.innerHeight * 0.8);
    });
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > window.innerHeight * 0.8 && !formVisible.current);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goManagement = () => {
    trackEvent("path_click", { path: "gestione", source: "servizi_hero" });
    scrollToSection("#villas");
  };
  const goServices = () => {
    trackEvent("path_click", { path: "servizi_singoli", source: "servizi_hero" });
    scrollToSection("#catalogo-servizi");
  };

  const requestService = (title) => {
    trackEvent("service_request_click", { service: title, source: "servizi" });
    setPrefill({ interest: contact.singleService, service: title });
    scrollToSection("#contatto");
  };

  const requestTier = (displayIdx) => {
    trackEvent("management_cta_click", { tier: [s.tiers.items[1], s.tiers.items[0], s.tiers.items[2]][displayIdx].name });
    setPrefill({ interest: contact.interests[displayIdx] });
    scrollToSection("#contatto");
  };

  const openHomeStaging = () => {
    trackEvent("service_cta_click", { service: "home-staging", source: "staging_section" });
    setOpenService(null);
    requestAnimationFrame(() => setOpenService("home-staging"));
    scrollToSection("#catalogo-servizi");
  };

  const pl = {
    it: {
      kicker: "Prove, non promesse",
      title: "Non soltanto promesse. Un metodo applicato sul campo.",
      stagingH: "Valorizzazione reale",
      stagingP: "Prima e dopo di un intervento di valorizzazione su un immobile gestito.",
      stagingCta: "Scopri il nostro Home Staging",
      reviewNote: "Recensioni di ospiti verificate: la prova dell'ospitalità che portiamo in ogni casa.",
    },
    en: {
      kicker: "Proof, not promises",
      title: "Not just promises. A method applied in the field.",
      stagingH: "Real enhancement",
      stagingP: "Before and after an enhancement intervention on a managed property.",
      stagingCta: "Discover our Home Staging",
      reviewNote: "Verified guest reviews: proof of the hospitality we bring to every home.",
    },
  }[lang];
  const featuredReview = s.reviews.items[2];

  return (
    <div data-testid="services-page" className="bg-warm">
      {/* 1 · HERO */}
      <section className="relative overflow-hidden bg-navy px-5 pb-24 pt-36 text-white sm:px-8 sm:pt-44">
        <img src={IMAGES.servicesHero} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.22]" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/40 to-navy" />
        <div className="relative mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}>
            <p className="kicker text-sky" data-testid="hero-eyebrow">
              <span className="h-px w-10 bg-sky" />
              {s.hero.eyebrow}
            </p>
            <h1 data-testid="hero-title" className="mt-6 font-display text-4xl font-semibold leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl">
              {s.hero.h1a}
              <span className="block italic text-sky">{s.hero.h1b}</span>
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">{s.hero.sub}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button data-testid="hero-cta-quiz" onClick={goManagement} className="btn-primary">
              {s.hero.cta}
              <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="hero-cta-services" onClick={goServices} className="btn-ghost">
              {s.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            data-testid="hero-trust"
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2.5 text-xs font-bold uppercase tracking-[0.16em] text-white/50"
          >
            {s.hero.trust.map((item) => (
              <span key={item} className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-sky" />
                {item}
              </span>
            ))}
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 1 }} className="mt-12 space-y-2.5 border-l-2 border-sky/50 pl-6">
            {s.hero.lines.map((line, i) => (
              <p key={line} className="text-sm font-semibold tracking-wide text-white/60 sm:text-base">
                <span className="mr-3 font-display italic text-sky">0{i + 1}</span>
                {line}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 2 · PROVE (subito dopo l'hero) */}
      <section data-testid="proof-section" className="bg-white px-5 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <SectionHead eyebrow={pl.kicker} title={pl.title} testid="proof-head" />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            <Reveal>
              <div className="flex h-full flex-col rounded-[1.75rem] bg-navy p-8 text-white" data-testid="proof-case">
                <span className="inline-flex w-fit rounded-full bg-white/10 px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-sky ring-1 ring-white/15">
                  {s.caseStudy.tag}
                </span>
                <p data-testid="case-study-big" className="mt-5 font-display text-6xl font-semibold text-sky">
                  +<Counter to={521} suffix="%" />
                </p>
                <div className="mt-6 space-y-4 border-t border-white/10 pt-5">
                  <div>
                    <div className="flex items-baseline justify-between text-sm font-bold">
                      <span className="text-white/70">{s.caseStudy.periodBefore}</span>
                      <span className="text-white/85">{s.caseStudy.nightsBefore}</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full w-[22%] rounded-full bg-white/30" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between text-sm font-bold">
                      <span className="text-sky">{s.caseStudy.periodAfter}</span>
                      <span className="text-white">{s.caseStudy.nightsAfter}</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
                      <motion.div initial={{ width: 0 }} whileInView={{ width: "100%" }} viewport={{ once: true }} transition={{ duration: 1.4, ease: EASE }} className="h-full rounded-full bg-sky" />
                    </div>
                  </div>
                </div>
                <p className="mt-5 text-xs italic leading-relaxed text-white/50">{s.caseStudy.note}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col overflow-hidden rounded-[1.75rem] ring-1 ring-navy/10" data-testid="proof-staging">
                <img src="/staging-a.jpg" alt={pl.stagingH} loading="lazy" className="h-48 w-full object-cover" />
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-display text-xl font-semibold text-navy">{pl.stagingH}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mute">{pl.stagingP}</p>
                  <button data-testid="proof-staging-cta" onClick={openHomeStaging} className="mt-5 inline-flex items-center gap-2 self-start text-xs font-extrabold uppercase tracking-[0.16em] text-navy transition-colors hover:text-med">
                    {pl.stagingCta}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex h-full flex-col rounded-[1.75rem] bg-warm p-8 ring-1 ring-navy/10" data-testid="proof-review">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-med">{s.reviews.verified}</p>
                <blockquote className="mt-4 flex-1 font-display text-lg italic leading-relaxed text-ink">
                  “{featuredReview.quote.length > 180 ? `${featuredReview.quote.slice(0, 180)}…` : featuredReview.quote}”
                </blockquote>
                <p className="mt-5 text-sm font-extrabold text-navy">
                  {featuredReview.name} · {featuredReview.from}
                </p>
                <p className="mt-1 text-xs text-mute">
                  {featuredReview.date}
                  {featuredReview.property ? ` · ${featuredReview.property}` : ""}
                </p>
                <p className="mt-4 border-t border-navy/10 pt-4 text-xs font-semibold text-mute">{pl.reviewNote}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3 · SCELTA DEL PERCORSO */}
      <section data-testid="services-paths" className="px-5 pb-20 sm:px-8">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-testid="paths-title">
              {PATHS[lang].title}
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {[
              { key: "a", icon: KeyRound, onClick: goManagement, testid: "services-path-a" },
              { key: "b", icon: Wrench, onClick: goServices, testid: "services-path-b" },
            ].map(({ key, icon: Icon, onClick, testid }) => {
              const p = PATHS[lang][key];
              return (
                <Reveal key={key}>
                  <button
                    data-testid={testid}
                    onClick={onClick}
                    className="group flex h-full w-full flex-col rounded-[1.75rem] bg-white p-8 text-left shadow-[0_25px_60px_-25px_rgba(15,43,58,0.35)] ring-1 ring-navy/10 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_35px_70px_-25px_rgba(15,43,58,0.45)]"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-sky transition-colors duration-300 group-hover:bg-med">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="mt-5 font-display text-2xl font-semibold leading-snug text-navy">{p.title}</span>
                    <span className="mt-3 block text-sm leading-relaxed text-mute">{p.sub}</span>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-navy transition-colors group-hover:text-med">
                      {p.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4 · MODELLI DI GESTIONE */}
      <section id="villas" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead center eyebrow={s.tiers.eyebrow} title={s.tiers.title} testid="tiers-head" />
          <div className="mt-16 grid items-stretch gap-8 lg:grid-cols-3">
            {[s.tiers.items[1], s.tiers.items[0], s.tiers.items[2]].map((tier, i) => {
              const featured = i === 0;
              const allPoints = [...tier.points, ...tier.extra];
              const shown = tier.extra.length && !tierOpen ? tier.points : allPoints;
              return (
                <Reveal key={tier.name} delay={i * 0.12} className="h-full">
                  <div data-testid={`tier-card-${i}`} className={`flex h-full flex-col rounded-[2rem] p-9 transition-[transform,box-shadow] duration-500 hover:-translate-y-2 ${featured ? "bg-navy text-white shadow-[0_40px_80px_-30px_rgba(15,43,58,0.5)] ring-1 ring-navy lg:-my-4 lg:py-12" : "bg-white ring-1 ring-navy/10 hover:shadow-[0_30px_60px_-25px_rgba(15,43,58,0.25)]"}`}>
                    {tier.badge && (
                      <span className="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-sky px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.15em] text-navy">
                        <Sparkles className="h-3.5 w-3.5" />
                        {tier.badge}
                      </span>
                    )}
                    <h3 className={`font-display text-2xl font-semibold ${featured ? "text-white" : "text-navy"}`}>{tier.name}</h3>
                    <p className={`mt-1 text-xs font-extrabold uppercase tracking-[0.2em] ${featured ? "text-sky" : "text-mute"}`}>{tier.tagline}</p>
                    <p className={`mt-5 text-sm font-semibold leading-relaxed ${featured ? "text-white/85" : "text-ink"}`}>{tier.desc}</p>
                    <div className={`mt-7 border-t pt-7 ${featured ? "border-white/15" : "border-navy/10"}`}>
                      <span className={`font-display text-4xl font-semibold ${featured ? "text-sky" : "text-navy"}`}>{tier.price}</span>
                      <span className={`mt-1 block text-xs font-semibold ${featured ? "text-white/55" : "text-mute"}`}>{tier.priceNote}</span>
                    </div>
                    <ul className="mt-7 flex-1 space-y-3">
                      {shown.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm font-medium leading-snug">
                          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${featured ? "text-sky" : "text-med"}`} strokeWidth={3} />
                          <span className={featured ? "text-white/80" : "text-ink"}>{p}</span>
                        </li>
                      ))}
                    </ul>
                    {tier.extra.length > 0 && (
                      <button data-testid="tier-more-button" onClick={() => setTierOpen((v) => !v)} className={`mt-5 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.15em] ${featured ? "text-sky" : "text-navy"}`}>
                        {tierOpen ? s.tiers.less : s.tiers.more}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${tierOpen ? "rotate-180" : ""}`} />
                      </button>
                    )}
                    <button
                      data-testid={`tier-cta-${i}`}
                      onClick={() => requestTier(i)}
                      className={`mt-8 w-full ${featured ? "btn-primary" : "btn-dark"}`}
                    >
                      {tier.cta}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 text-center">
              <button data-testid="tiers-valuation-cta" onClick={() => scrollToSection("#contatto")} className="btn-dark">
                {lang === "en" ? "Request a valuation of your home" : "Richiedi una valutazione della tua casa"}
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mx-auto mt-8 max-w-2xl text-sm italic text-mute">{s.tiers.note}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5 · QUIZ di qualificazione */}
      <section id="quiz" className="bg-sand py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <Quiz
              content={s.quiz}
              onComplete={(answers) => {
                setQuizAnswers(answers);
                setPrefill({ openDetailed: true });
                scrollToSection("#contatto");
              }}
            />
          </Reveal>
        </div>
      </section>

      {/* 6 · CATALOGO OG STAY SERVICES */}
      <Reveal>
        <ServicesCatalog lang={lang} onRequest={requestService} openServiceId={openService} />
      </Reveal>

      {/* 7 · METODO */}
      <section id="metodo" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow={s.method.eyebrow} title={s.method.title} titleItalic={s.method.titleItalic} testid="method-head" />
          <div className="mt-16 hidden gap-4 lg:grid lg:grid-cols-6">
            {s.method.steps.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.08} className="h-full">
                <div
                  tabIndex={0}
                  data-testid={`method-step-${i}`}
                  className="group flex h-full min-h-[15rem] flex-col rounded-[1.5rem] bg-white p-6 ring-1 ring-navy/10 outline-none transition-[background-color,transform,box-shadow] duration-500 focus-within:-translate-y-2 focus-within:bg-navy hover:-translate-y-2 hover:bg-navy focus-visible:ring-2 focus-visible:ring-sky hover:shadow-[0_24px_50px_-20px_rgba(15,43,58,0.5)]"
                >
                  <span className="font-display text-3xl font-semibold text-sky">{step.n}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-navy transition-colors duration-500 group-focus-within:text-white group-hover:text-white">{step.t}</h3>
                  <ul className="mt-3 max-h-0 space-y-1.5 overflow-hidden opacity-0 transition-[max-height,opacity] duration-700 group-focus-within:max-h-56 group-focus-within:opacity-100 group-hover:max-h-56 group-hover:opacity-100">
                    {step.items.map((it) => (
                      <li key={it} className="text-xs font-semibold capitalize text-white/70">· {it}</li>
                    ))}
                  </ul>
                  <p className="mt-auto pt-3 text-[11px] font-bold uppercase tracking-[0.15em] text-mute transition-opacity duration-300 group-focus-within:opacity-0 group-hover:opacity-0">
                    {step.items.length} step
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 space-y-3 lg:hidden">
            {s.method.steps.map((step, i) => (
              <details key={step.n} data-testid={`method-step-m-${i}`} className="group rounded-2xl bg-white px-6 py-4 ring-1 ring-navy/10">
                <summary className="flex cursor-pointer list-none items-center gap-4 [&::-webkit-details-marker]:hidden">
                  <span className="font-display text-2xl font-semibold text-sky">{step.n}</span>
                  <span className="flex-1 font-display text-lg font-semibold text-navy">{step.t}</span>
                  <ChevronDown className="h-4 w-4 text-mute transition-transform duration-300 group-open:rotate-180" />
                </summary>
                <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 pb-2">
                  {step.items.map((it) => (
                    <li key={it} className="text-sm font-semibold capitalize text-mute">· {it}</li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 8 · HÔTELLERIE */}
      <section className="bg-navy py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead dark eyebrow={s.hotel.eyebrow} title={s.hotel.title} sub={s.hotel.text} testid="hotel-head" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {s.hotel.standards.map((st, i) => {
              const Icon = HOTEL_ICONS[i];
              return (
                <Reveal key={st.t} delay={i * 0.07}>
                  <div data-testid={`hotel-standard-${i}`} className="group rounded-[1.75rem] bg-med/40 p-8 ring-1 ring-white/10 transition-colors duration-500 hover:bg-med/60">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky/15 text-sky transition-transform duration-500 group-hover:scale-110">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 text-xs font-extrabold uppercase tracking-[0.3em] text-sky">{st.t}</h3>
                    <p className="mt-2.5 text-base font-semibold leading-relaxed text-white/85">{st.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2} className="mt-14 text-center">
            <p className="mx-auto inline-block border-y border-sky/30 py-5 font-display text-2xl italic text-sand sm:text-3xl">{s.hotel.closing}</p>
          </Reveal>
        </div>
      </section>

      {/* 9 · PRIMA/DOPO */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <SectionHead title={s.staging.title} sub={`${s.staging.text1} ${s.staging.text2}`} testid="staging-head" />
            <Reveal delay={0.15}>
              <button data-testid="staging-cta" onClick={openHomeStaging} className="btn-dark mt-8">
                {pl.stagingCta}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <BeforeAfter before="/staging-b.webp" after="/staging-a.jpg" labels={s.staging} />
            <p className="mt-6 text-center font-display text-xl italic text-med">{s.staging.highlight}</p>
          </Reveal>
        </div>
      </section>

      {/* 10 · BUROCRAZIA / AVVIAMENTO */}
      <section className="bg-sand py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow={s.buro.eyebrow} title={s.buro.title} sub={s.buro.text} testid="buro-head" />
          <div className="mt-12 flex flex-wrap gap-2.5">
            {s.buro.items.map((item, i) => (
              <Reveal key={item} delay={i * 0.03}>
                <span data-testid={`buro-item-${i}`} className="inline-block rounded-full bg-white px-5 py-2.5 text-sm font-bold text-navy ring-1 ring-navy/10">
                  {item}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl text-sm italic leading-relaxed text-mute">{s.buro.note}</p>
            <button data-testid="buro-cta" onClick={() => scrollToSection("#contatto")} className="btn-dark mt-7">
              {s.buro.cta}
              <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* 11 · MANDATO */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHead eyebrow={s.mandate.eyebrow} title={s.mandate.title} testid="mandate-eyebrow" />
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-mute sm:text-lg">{s.mandate.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-mute sm:text-lg">{s.mandate.p2}</p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-10 border-l-4 border-sky pl-6 font-display text-2xl italic leading-snug text-navy sm:text-3xl">{s.mandate.highlight}</p>
            <p className="mt-8 rounded-2xl bg-sand px-6 py-5 text-sm italic leading-relaxed text-mute ring-1 ring-navy/5">{s.mandate.note}</p>
          </Reveal>
        </div>
      </section>

      {/* 12 · SIMULATORE */}
      <section className="bg-sand py-24 sm:py-32">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <Calculator content={s.calc} />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 text-center">
              <button data-testid="calc-cta" onClick={() => scrollToSection("#contatto")} className="btn-dark">
                {s.calc.cta}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 13 · PORTALE PROPRIETARIO */}
      <section className="bg-sand py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <span className="absolute -top-4 left-6 z-10 rounded-full bg-navy px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-sky shadow-lg">
                {s.portal.demoTag}
              </span>
              <div data-testid="portal-mockup" className="overflow-hidden rounded-[1.75rem] bg-white shadow-[0_40px_80px_-30px_rgba(15,43,58,0.4)] ring-1 ring-navy/10">
                <div className="flex items-center gap-1.5 border-b border-navy/5 bg-warm px-5 py-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-navy/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-navy/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-sky" />
                  <span className="ml-3 text-[10px] font-bold uppercase tracking-widest text-mute">vr.krossbooking.com/ogstay</span>
                </div>
                <div className="grid grid-cols-2 gap-3 p-6 sm:grid-cols-4">
                  {s.portal.stats.map((st) => (
                    <div key={st.label} className="rounded-2xl bg-sand p-4">
                      <p className="font-display text-xl font-semibold text-navy">{st.value}</p>
                      <p className="mt-1 text-[10px] font-extrabold uppercase tracking-wider text-mute">{st.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex h-28 items-end gap-2 px-6 pb-6">
                  {[35, 55, 42, 70, 58, 85, 64, 92, 78, 100, 88, 96].map((hVal, i) => (
                    <motion.div key={i} initial={{ height: 0 }} whileInView={{ height: `${hVal}%` }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.05, ease: EASE }} className={`flex-1 rounded-t-md ${i % 3 === 2 ? "bg-sky" : "bg-navy/85"}`} />
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHead title={s.portal.title} testid="portal-head" />
            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
              {s.portal.features.map((fItem) => (
                <li key={fItem} className="flex items-center gap-2.5 text-sm font-semibold text-ink">
                  <Check className="h-4 w-4 shrink-0 text-med" strokeWidth={3} />
                  {fItem}
                </li>
              ))}
            </ul>
            <Reveal delay={0.15}>
              <a data-testid="portal-cta" href={LINKS.ownerPortal} target="_blank" rel="noopener noreferrer" className="btn-primary mt-9">
                <KeyRound className="h-4 w-4" />
                {s.portal.cta}
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 14 · PRESENZA LOCALE */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <SectionHead eyebrow={s.local.eyebrow} title={s.local.title} testid="local-head" />
            <Reveal delay={0.1}>
              <p className="mt-7 text-base leading-relaxed text-mute sm:text-lg">{s.local.p1}</p>
              <p className="mt-3 text-base leading-relaxed text-mute sm:text-lg">{s.local.p2}</p>
              <p className="mt-6 font-display text-xl italic leading-snug text-med sm:text-2xl">{s.local.p3}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {s.local.depts.map((d, i) => {
                const Icon = DEPT_ICONS[i];
                return (
                  <Reveal key={d} delay={i * 0.05}>
                    <span data-testid={`dept-${i}`} className="flex items-center gap-2.5 rounded-2xl bg-white px-4 py-3.5 text-sm font-bold text-navy ring-1 ring-navy/10">
                      <Icon className="h-4 w-4 shrink-0 text-med" />
                      {d}
                    </span>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-6 text-xs font-extrabold uppercase tracking-[0.25em] text-mute">{s.local.closing}</p>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-t-[10rem] rounded-b-[2rem] ring-1 ring-navy/10">
              <img src={IMAGES.servicesAbout} alt="Team OGStay" loading="lazy" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 15 · SELETTIVITÀ */}
      <section className="bg-sand py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <div className="rounded-[2rem] border-2 border-dashed border-navy/20 p-10 sm:p-14">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">{s.select.title}</h2>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-mute">{s.select.sub}</p>
              <p className="mt-7 text-xs font-extrabold uppercase tracking-[0.2em] text-med">{s.select.intro}</p>
              <ul className="mx-auto mt-4 grid max-w-lg gap-2.5 text-left sm:grid-cols-2">
                {s.select.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-mute">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-med" strokeWidth={3} />
                    {it}
                  </li>
                ))}
              </ul>
              <button data-testid="select-cta" onClick={() => scrollToSection("#contatto")} className="btn-primary mt-8">
                {s.select.cta}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 16 · RECENSIONI VERIFICATE */}
      <ReviewsSection content={s.reviews} />

      {/* 17 · FAQ (due gruppi) */}
      <section className="px-5 py-24 sm:px-8 sm:py-32">
        <div className="mx-auto max-w-3xl">
          <SectionHead center eyebrow="FAQ" title={s.faq.title} testid="faq-head" />
          {s.faq.groups.map((group, gi) => (
            <div key={group.label} className={gi === 0 ? "mt-12" : "mt-14"}>
              <Reveal>
                <p data-testid={`faq-group-${gi}`} className="text-xs font-extrabold uppercase tracking-[0.22em] text-med">
                  {group.label}
                </p>
              </Reveal>
              <div className="mt-5 space-y-3">
                {group.items.map((it, i) => (
                  <Reveal key={it.q} delay={i * 0.04}>
                    <details data-testid={`faq-item-${gi}-${i}`} className="group rounded-2xl bg-white px-6 py-5 ring-1 ring-navy/10">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                        <span className="font-display text-lg font-semibold text-navy">{it.q}</span>
                        <ChevronDown className="h-5 w-5 shrink-0 text-mute transition-transform duration-300 group-open:rotate-180" />
                      </summary>
                      <p className="mt-3 text-sm leading-relaxed text-mute">{it.a}</p>
                    </details>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 18 · CONTATTO FINALE */}
      <ContactSection
        lang={lang}
        prefill={prefill}
        s={s}
        detailed={<FinalForm f={f} lang={lang} quizAnswers={quizAnswers} />}
      />

      {/* 19 · STICKY MOBILE */}
      <AnimatePresence>
        {showSticky && (
          <motion.div
            data-testid="sticky-mobile-cta"
            initial={{ y: 90 }}
            animate={{ y: 0 }}
            exit={{ y: 90 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-2 border-t border-navy/10 bg-white/95 px-4 py-3 backdrop-blur-md lg:hidden"
          >
            <a
              data-testid="sticky-whatsapp"
              href={`${LINKS.whatsapp}?text=${encodeURIComponent(contact.waMessage())}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click_owner", { source: "servizi_sticky" })}
              className="btn-primary flex-1 !py-3 text-[12px]"
            >
              {contact.whatsapp}
            </a>
            <button data-testid="sticky-cta" onClick={() => scrollToSection("#contatto")} className="btn-secondary flex-1 !py-3 text-[12px]">
              {s.sticky.cta}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
